import { Surface, ErrorState, TableLoadingOverlay, Alert } from '@/components'
import { ViewType } from '@/stores/useViewAsToggle/useViewAsToggle'
import React from 'react'
import { useSustainabilityApi } from '@/hooks/useApi/useSustainabilityApi/useSustainabilityApi'
import { TotalEmissionsChart } from './TotalEmissionsChart/TotalEmissionsChart'
import { TotalEmissionsTableContainer } from './TotalEmissionsTable/TotalEmissionsTableContainer'
import { ContentPlaceholder } from '@/components/molecules'
import { useTranslation } from 'react-i18next'
import { ALERT_DESCRIPTION_MAP } from '@/utils/constants'
import { TotalEmissionsReportSettings } from '@/hooks'
import { GetSustainabilityTotalEmissionsRequestParams } from '@/hooks'
import { useSustainabilityDisplayManager } from '@/features/Sustainability/components/TotalEmissionsDrawer/useSustainabilityDisplayManager'
import { mapSustainabilityParamsToFilters } from '../../utils/mapSustainabilityParamsToFilters'

type TotalEmissionsViewProps = {
    activeView: ViewType
    reportSettings: TotalEmissionsReportSettings
    filters: GetSustainabilityTotalEmissionsRequestParams
}

export const TotalEmissionsView: React.FC<TotalEmissionsViewProps> = ({
    activeView,
    reportSettings,
    filters,
}) => {
    const { t } = useTranslation('translations')

    const mappedFilters = mapSustainabilityParamsToFilters(filters)
    const { showAlert } = useSustainabilityDisplayManager(
        reportSettings,
        mappedFilters
    )

    const { useTotalEmissionsQuery } = useSustainabilityApi()
    const { data, isError, isLoading, isFetching, isFetched } =
        useTotalEmissionsQuery(filters)

    const hasData =
        Array.isArray(data?.items) &&
        data.items.length > 0 &&
        data.items.some((item) => item.value !== 0)

    const loadingMessage =
        activeView === 'chart'
            ? t('analytics.common.report_chart_loading')
            : t('analytics.common.report_table_loading')

    const emptyStateDescription = t(
        'analytics.common.report_empty_state.description'
    )

    if (isError) {
        return <ErrorState description={t('analytics.common.report_error')} />
    }

    if (isFetched && !hasData) {
        return (
            <ContentPlaceholder
                title={t('analytics.common.report_empty_state.title')}
                description={emptyStateDescription}
                illustrationSrc="/assets/empty-state-illustration.svg"
                name="empty-state"
            />
        )
    }

    return (
        <div className="relative" data-testid="total-emissions-view">
            {activeView === 'chart' ? (
                <div className="grid h-full">
                    <Surface className="min-h-chartSurfaceMinHeight min-w-minWidgetWidth">
                        <div className="flex h-full flex-col gap-4">
                            {showAlert && (
                                <Alert
                                    variant="info"
                                    description={t(
                                        ALERT_DESCRIPTION_MAP[
                                            reportSettings.type
                                        ]
                                    )}
                                />
                            )}
                            <div className="flex h-full flex-col justify-center">
                                <TotalEmissionsChart
                                    data={data?.items ?? []}
                                    startDate={filters.startDate}
                                    endDate={filters.endDate}
                                    total={data?.total ?? 0}
                                    type={reportSettings.type}
                                />
                            </div>
                        </div>
                    </Surface>
                    <TableLoadingOverlay
                        show={isLoading || isFetching || !isFetched}
                        title={loadingMessage}
                    />
                </div>
            ) : (
                <TotalEmissionsTableContainer
                    data={data?.items ?? []}
                    isLoading={isLoading || isFetching || !isFetched}
                    type={reportSettings.type}
                    startDate={filters.startDate}
                    endDate={filters.endDate}
                    total={data?.total ?? 0}
                    key={reportSettings.type}
                />
            )}
        </div>
    )
}
